import React, { Fragment, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";

import PageTitle from "../../layouts/PageTitle";
import CustomTextarea from "../../components/Forms/CustomInputs/CustomTextarea";
import CustomImageUpload from "../../components/Forms/CustomInputs/CustomImageUpload";
import { removeImageFromPath } from "../../components/Forms/FormHandler";
import { toast } from "react-toastify";
import { apiCaller } from "../../../services/AxiosInstance";
import CustomTextbox from "../../components/Forms/CustomInputs/CustomTextbox";

const validationSchema = Yup.object().shape({
  title: Yup.string().required("Please Enter Title"),
  banner: Yup.mixed().required("Please Upload Image"),
  postStartDate: Yup.date().required("Please Enter Post Start Date"),
  postEndDate: Yup.date()
    .required("Please Enter Post End Date")
    .test(
      "isAfterStartDate",
      "End date must be equal to or after start date",
      function (value) {
        const { postStartDate } = this.parent;
        if (!value || !postStartDate) return true;
        return new Date(value) >= new Date(postStartDate);
      }
    ),
  description: Yup.string()
    .test(
      "maxWords",
      "Description must be at most 200 words",
      function (value) {
        if (!value) return true;
        const wordCount = value.trim().split(/\s+/).length;
        return wordCount <= 200;
      }
    ),
});

const AddUpdateAppPost = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [saveStep, setSaveStep] = useState(null);


  let initialValues = {
    postStartDate: "",
    postEndDate: "",
    description: "",
    banner: "",
    title: "",
  };

  if (state) {
    initialValues = {
      ...state,
    };
  }

  const uploadFileToServer = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await apiCaller({
        method: "post",
        url: "upload",
        params: null,
        data: formData,
      });
      if (res?.data?.data?.url) {
        return res?.data?.data?.url;
      } else {
        throw new Error("Upload failed");
      }
    } catch (err) {
      throw err;
      console.error(err);
    }
  }

  return (
    <Fragment>
      <PageTitle />

      <div className="row">
        <div className="col-lg-6">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">
                {state?._id ? "Edit" : "Add New"} Post
              </h4>
            </div>
            <div className="card-body">
              <div className="basic-form">
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={async (values, { setSubmitting }) => {
                    const finishSubmit = () => {
                      setSaveStep(null);
                      setSubmitting(false);
                    };
                    try {
                      setSaveStep(null);
                      setSubmitting(true);
                      const keysToCheck = ["banner"];
                      for (const [key, value] of Object.entries(values)) {
                        try {
                          if (keysToCheck.findIndex((k) => k === key) !== -1) {
                            const oldValue = initialValues[key];
                            const newValue = values[key];

                            if (oldValue && !newValue) {
                              console.log("Removed:", key, "old path:", oldValue);
                              setSaveStep("uploading");
                              await removeImageFromPath(oldValue);
                            }
                          }
                        } catch (error) {
                          toast.error("Error removing file");
                          console.error(error);
                          finishSubmit();
                          return;
                        }
                        if (value instanceof File) {
                          const file = value;
                          try {
                            setSaveStep("uploading");
                            const url = await uploadFileToServer(file);
                            values[key] = url;
                          } catch (err) {
                            toast.error("Error uploading file");
                            console.error(err);
                            finishSubmit();
                            return;
                          }
                        }
                      }
                      let response;

                      try {
                        setSaveStep("saving");
                        if (state) {
                          response = await apiCaller({
                            method: "put",
                            url: `app-feed/${state._id}`,
                            data: values
                          });
                          console.log("Update response:", response);
                          console.log("Update response status:", response?.status);
                        } else {
                          response = await apiCaller({
                            method: "post",
                            url: "app-feed",
                            data: values
                          });
                          console.log("Create response:", response);
                          console.log("Create response status:", response?.status);
                        }

                        if (response?.status === 200 || response?.status === 201) {
                          console.log("API call successful, navigating...");

                          const isCreate = !state?._id;
                          setTimeout(() => {
                            navigate("/app-feed", {
                              replace: true,
                              ...(isCreate
                                ? { state: { fromAppFeedSave: true } }
                                : {}),
                            });
                          }, 100);
                          finishSubmit();
                        } else {
                          console.log("API call failed with status:", response?.status);
                          toast.error("Error saving post. Please try again.");
                          finishSubmit();
                        }
                      } catch (apiError) {
                        console.log("API Error caught:", apiError);
                        console.log("API Error message:", apiError?.message);
                        console.log("API Error response:", apiError?.response);
                        toast.error("Error saving post. Please try again.");
                        finishSubmit();
                        return;
                      }
                    } catch (error) {
                      console.log("Outer catch block - snvuisbevsevsdvs322", error);
                      console.log("Outer catch error message:", error?.message);
                      console.log("Outer catch error stack:", error?.stack);
                      toast.error("Error saving post. Please try again.");
                      finishSubmit();
                    }
                  }}
                >
                  {({
                    values,
                    errors,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                    setFieldValue,
                    touched
                  }) => (
                    <form onSubmit={handleSubmit}>
                      <div className="row">
                        <CustomImageUpload
                          fieldName="banner"
                          value={values.banner}
                          error={errors.banner}
                          label={"Image"}
                          classes={"col-md-12"}
                          handleBlur={handleBlur}
                          inputType={"text"}
                          handleChange={handleChange}
                          setFieldValue={setFieldValue} />

                        <CustomTextbox
                          fieldName={`title`}
                          value={values?.title}
                          error={touched?.title ? errors?.title : ""}
                          label={"Title"}
                          classes={"col-md-12"}
                          handleBlur={handleBlur}
                          inputType={"text"}
                          handleChange={handleChange}
                        />

                        <CustomTextarea
                          errors={errors}
                          setFieldValue={setFieldValue}
                          handleBlur={handleBlur}
                          values={values}
                        />

                        <div
                          className={`form-group col-md-6 mb-3 ${errors ? "is-invalid" : "is-valid"
                            }`}
                        >
                          <label className="text-label d-block">Post Start Date</label>
                          <div className="d-block">
                            <input
                              type="date"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={
                                values.postStartDate
                                  ? new Date(values.postStartDate).toISOString().slice(0, 10)
                                  : ""
                              }
                              className="input form-control"
                              name="postStartDate"
                            />
                          </div>

                          {errors && errors.postStartDate ? (
                            <div
                              id={`val-postStartDate-error`}
                              class="invalid-feedback animated fadeInUp"
                              style={{ display: "block" }}
                            >
                              {errors.postStartDate}
                            </div>
                          ) : null}
                        </div>

                        <div
                          className={`form-group col-md-6 mb-3 ${errors ? "is-invalid" : "is-valid"
                            }`}
                        >
                          <label className="text-label d-block">Post End Date</label>
                          <div className="d-block">
                            <input
                              type="date"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={
                                values.postEndDate
                                  ? new Date(values.postEndDate).toISOString().slice(0, 10)
                                  : ""
                              }
                              className="input form-control"
                              name="postEndDate"
                            />
                          </div>

                          {errors && errors.postEndDate ? (
                            <div
                              id={`val-postEndDate-error`}
                              class="invalid-feedback animated fadeInUp"
                              style={{ display: "block" }}
                            >
                              {errors.postEndDate}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="float-end">
                        <button
                          type="submit"
                          className="btn me-2 btn-primary d-inline-flex align-items-center gap-2"
                          disabled={isSubmitting}
                          aria-busy={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              />
                              <span>
                                {saveStep === "uploading"
                                  ? "Uploading image..."
                                  : saveStep === "saving"
                                    ? "Saving post..."
                                    : "Please wait..."}
                              </span>
                            </>
                          ) : (
                            "Save"
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AddUpdateAppPost;
